import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authApi } from './api';
import { useAuth } from './AuthContext';
import { Eye, EyeOff, Loader2, CheckCircle2 } from 'lucide-react';
import { logger } from '../lib/logger';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_apex-athletes-mind/artifacts/ewmkb3wq_IMG_2112.JPG';

export default function ResetPassword() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('token') || '', [params]);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError('This reset link is missing a token. Please request a new one.');
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords don\'t match.'); return; }
    setSubmitting(true);
    try {
      const { user } = await authApi.resetPassword(token, password);
      setUser(user);
      setDone(true);
      setTimeout(() => navigate('/app/dashboard', { replace: true }), 1200);
    } catch (err) {
      logger.error('reset-password failed', err);
      setError(err.message || 'Could not reset your password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0d0e] via-[#0f1416] to-[#0a0d0e] text-white flex items-center justify-center px-5 py-10" data-testid="reset-page">
      <div className="absolute inset-0 bg-[radial-gradient(800px_circle_at_30%_40%,rgba(0,76,84,0.18),transparent_60%)] pointer-events-none" />
      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-7">
          <div className="w-16 h-16 rounded-2xl overflow-hidden bg-white shadow-2xl mb-4">
            <img src={LOGO_URL} alt="Apex Athletic Mind" className="w-full h-full object-contain" />
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-2">
            Set a new password
          </h1>
          <p className="text-white/55 text-[14px] leading-relaxed max-w-[34ch]">
            Choose something strong you'll remember.
          </p>
        </div>

        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 sm:p-7">
          {done ? (
            <div className="text-center space-y-3 py-2" data-testid="reset-success">
              <div className="w-12 h-12 rounded-full bg-[#004C54]/30 border border-[#004C54]/50 flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-5 w-5 text-[#4FBCC7]" />
              </div>
              <div className="font-heading text-lg font-bold">Password updated</div>
              <p className="text-[14px] text-white/65">Signing you in…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
              <div>
                <Label htmlFor="reset-password" className="text-[12px] text-white/60 mb-1.5 block">New password</Label>
                <div className="relative">
                  <Input
                    id="reset-password"
                    type={showPw ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="h-11 pr-11 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#4FBCC7]"
                    disabled={submitting || !token}
                    data-testid="reset-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-white/50 hover:text-white"
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="reset-confirm" className="text-[12px] text-white/60 mb-1.5 block">Confirm new password</Label>
                <Input
                  id="reset-confirm"
                  type={showPw ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repeat password"
                  className="h-11 bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#4FBCC7]"
                  disabled={submitting || !token}
                  data-testid="reset-confirm"
                  required
                />
              </div>

              {error && (
                <div className="text-[13px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="reset-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting || !token}
                className="w-full h-11 bg-[#004C54] hover:bg-[#003a40] text-white font-semibold"
                data-testid="reset-submit"
              >
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Updating…</> : 'Update password'}
              </Button>

              <p className="text-center text-[12px] text-white/45 mt-3">
                <Link to="/app/login" className="text-[#4FBCC7] hover:text-white font-semibold" data-testid="reset-back-to-login">
                  ← Back to sign in
                </Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
